import {
  ReactElement,
  ElementRef,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react';
import { useFormik, FormikProvider, FormikHelpers } from 'formik';
import { Stack } from '@mui/material';
import Map, {
  NavigationControl,
  FullscreenControl,
  Marker,
  MarkerDragEvent,
} from 'react-map-gl';
import { useSnackbar } from 'notistack';

import { TOKEN } from '~utils/mapbox';
import {
  useLazyRequest,
  useCurrentCapacityTypeInfo,
  useTypePartnerCompany,
} from '~hooks/index';
import { createItem } from '~services/item';
import { Marker as MarkerIcon } from '~components/Icons';
import { ModeSateliteControl } from '~components/MapCustomControl';
import { MapMarkerDragHint, RouteErrorsList } from '~components/index';
import { CustomAutocompleteRef } from '~components/CustomAutocomplete/types';

import { OrderFormProvider } from './OrderFormContext';
import {
  OrderFormDestinationInformationSection,
  OrderFormContactInformationSection,
  OrderFormSecurityToolsSection,
  OrderFormVisitDetailsSection,
  OrderFormArticlesSection,
} from './Sections';
import { OrderFormProps } from './types';
import {
  FIELDS_NAME,
  getInitialValues,
  validationSchema,
  getBodyRequestAddOrder,
  getParseListErrors,
} from './utils';
import {
  OrderFormContent,
  OrderFormInputsContent,
  OrderFormMapContainer,
  OrderFormSubmitButton,
} from './styles';
import PlacesAutocomplete from '../PlacesAutocomplete';

const OrderForm = ({
  itemData,
  operationsOptions,
  priorityOptions,
  skillsNeededTypes,
  servicesTypes,
}: OrderFormProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const { isCarrier } = useTypePartnerCompany();

  const [isCompletedRequest, setIsCompletedRequest] = useState(false);

  const [, loadingAddOrderRequest, , executeAddOrder] = useLazyRequest({
    request: createItem,
    withPostSuccess: () => {
      setIsCompletedRequest(true);
      enqueueSnackbar('Pedido creado correctamente', { variant: 'success' });
    },
    withPostFailure: () => {
      setIsCompletedRequest(false);
      enqueueSnackbar('Ha ocurrido un error, intente nuevamente', {
        variant: 'error',
      });
    },
  });

  const onSubmit = useCallback(
    async (
      values: ReturnType<typeof getInitialValues>,
      { setSubmitting }: FormikHelpers<ReturnType<typeof getInitialValues>>,
    ) => {
      await executeAddOrder(getBodyRequestAddOrder(values));

      setSubmitting(false);
    },
    [executeAddOrder],
  );

  const formikBag = useFormik({
    initialValues: getInitialValues(itemData, servicesTypes),
    validationSchema,
    onSubmit,
  });

  const {
    values,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    isSubmitting,
    resetForm,
    submitCount,
    errors,
  } = formikBag;

  const currentCapacities = useCurrentCapacityTypeInfo();

  const errorsList = useMemo(
    () => getParseListErrors(errors, currentCapacities),
    [errors, currentCapacities],
  );

  const placesAutocompleteRef =
    useRef<ElementRef<typeof PlacesAutocomplete>>(null);

  const skillsNeededAutocompleteRef =
    useRef<CustomAutocompleteRef<string, true>>(null);

  const serviceTypeListAutocompleteRef =
    useRef<CustomAutocompleteRef<string, false>>(null);

  useEffect(() => {
    if (isCompletedRequest) {
      resetForm({ values: getInitialValues(null, []) });
      placesAutocompleteRef.current?.resetAutocomplete();
      skillsNeededAutocompleteRef.current?.resetAutocomplete();
      serviceTypeListAutocompleteRef.current?.resetAutocomplete();

      setIsCompletedRequest(false);
    }
  }, [isCompletedRequest, resetForm, servicesTypes]);

  const handleOnDragEndEvent = useCallback(
    (e: MarkerDragEvent): void => {
      setFieldValue(FIELDS_NAME.ADDRESS_COORDS, {
        lat: e.lngLat.lat,
        lng: e.lngLat.lng,
      });
      setFieldTouched(FIELDS_NAME.ADDRESS_COORDS);
    },
    [setFieldValue, setFieldTouched],
  );

  return (
    <FormikProvider value={formikBag}>
      <OrderFormProvider
        value={{
          itemData,
          refs: {
            placesAutocompleteRef,
            skillsNeededAutocompleteRef,
            serviceTypeListAutocompleteRef,
          },
          enumSelects: {
            operationTypes: operationsOptions,
            priorityTypes: priorityOptions,
            skillsNeededTypes,
            servicesTypes,
          },
        }}
      >
        <Stack spacing={2} component="form" onSubmit={handleSubmit}>
          <OrderFormContent direction="row" spacing={2}>
            <OrderFormInputsContent spacing={2} flex={1}>
              <OrderFormDestinationInformationSection />

              <OrderFormContactInformationSection />

              {isCarrier && <OrderFormSecurityToolsSection />}

              <OrderFormVisitDetailsSection />

              <OrderFormArticlesSection />
            </OrderFormInputsContent>

            <Stack spacing={2} flex={0.7}>
              {values[FIELDS_NAME.ADDRESS_COORDS] && (
                <OrderFormMapContainer>
                  <Map
                    mapboxAccessToken={TOKEN}
                    mapStyle="mapbox://styles/mapbox/light-v10"
                    initialViewState={{
                      longitude: values[FIELDS_NAME.ADDRESS_COORDS]?.lng,
                      latitude: values[FIELDS_NAME.ADDRESS_COORDS]?.lat,
                      zoom: 14,
                    }}
                    attributionControl={false}
                  >
                    <FullscreenControl position="bottom-left" />
                    <NavigationControl
                      position="bottom-left"
                      showCompass={false}
                    />
                    <ModeSateliteControl position="bottom-left" />

                    <Marker
                      longitude={values[FIELDS_NAME.ADDRESS_COORDS]?.lng}
                      latitude={values[FIELDS_NAME.ADDRESS_COORDS]?.lat}
                      onDragEnd={handleOnDragEndEvent}
                      anchor="bottom"
                      draggable
                    >
                      <MarkerIcon color="primary" fontSize="large" />
                    </Marker>
                    <MapMarkerDragHint />
                  </Map>
                </OrderFormMapContainer>
              )}
            </Stack>
          </OrderFormContent>

          <Stack>
            {submitCount > 0 && errorsList.length > 0 && (
              <RouteErrorsList
                title="Errores encontrados"
                errors={errorsList}
              />
            )}

            <Stack spacing={1} direction="row">
              <OrderFormSubmitButton
                type="submit"
                variant="contained"
                color="primary"
                loading={loadingAddOrderRequest || isSubmitting}
              >
                Agregar
              </OrderFormSubmitButton>
            </Stack>
          </Stack>
        </Stack>
      </OrderFormProvider>
    </FormikProvider>
  );
};

export default OrderForm;
